/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { message } from 'antd';
import { useContainer } from '../StoreProvider';
import { getTotalPrice } from '../utils';
import { getRewards } from '../utils';
import styled from './index.module.scss';
import { useLang } from '@/i18n';
import { receiveForMemberStore } from '@/service/membersstore';
import getLocal from '@/app/utils/getLocal';
import { showErrorMessage } from '@/components/EcoCredits/utils';
import gtm from '@/utils/gtm';
import type { PriceInfoType } from '@/components/MembersStore/types';

export interface CouponProps extends Pick<PriceInfoType, 'receiveStatus' | 'useStatus'> {
    name: string;
}

message.config({
    top: 150,
});

const Coupon = ({ receiveStatus, useStatus, name }: CouponProps) => {
    const { locale } = getLocal();
    const { t } = useLang('membersstore');
    const { priceInfo, onChange } = useContainer();

    const handleCopyCompatibility = (code: string) => {
        const textArea = document.createElement('textarea');
        textArea.value = code || '';
        textArea.style.position = 'absolute';
        textArea.style.opacity = '0';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        new Promise(() => {
            const copyStatus = document.execCommand('copy');
            if (!copyStatus) {
                message.error('Copy error');
            }
            textArea.remove();
        });
    };

    const copyCode = (code: string) => {
        window.navigator.clipboard.writeText(code).catch(() => {
            handleCopyCompatibility(code);
        });
    };

    const claimCoupon = async () => {
        if (priceInfo) {
            const { discountId, version } = priceInfo;

            try {
                const res = await receiveForMemberStore({ discountId, discountVersion: version }, locale);
                if (res.code === '0') {
                    gtm.push({
                        event: 'uaEvent',
                        event_name: 'activity_coupon',
                        salePlatform: 'Shopify',
                        button_name: `${t('button_clain_now')}_Shopify`,
                    });
                    message.success(t('copy_tip'));
                    copyCode(res.data.coupon);
                    getTotalPrice(
                        locale,
                        (data) => {
                            onChange?.('priceInfo', data);
                        },
                        t,
                    );
                    getRewards(
                        locale,
                        (data) => {
                            onChange?.('couponList', data);
                        },
                        t,
                    );
                } else {
                    showErrorMessage(res.code, t);
                }
            } catch (err) {}
        }
    };

    const copyCouponCode = () => {
        if (!(priceInfo && priceInfo?.coupon)) return;
        gtm.push({
            event: 'uaEvent',
            event_name: 'copy_promo_code',
            titleName: name,
            buttonName: 'Apply',
            position: 'Toptip',
        });
        copyCode(priceInfo.coupon);
        message.success(t('copy_tip'));
    };

    const Button = React.useMemo(() => {
        if (receiveStatus === 1) {
            return (
                <div className="coupon-button" onClick={claimCoupon}>
                    {t('button_clain_now')}
                </div>
            );
        } else if (receiveStatus === 2 && useStatus === 'UNUSED') {
            return (
                <div className="coupon-button" onClick={copyCouponCode}>
                    {t('button_apply')}
                </div>
            );
        } else if (receiveStatus === 2 && useStatus === 'USED') {
            return <div className="coupon-button used">{t('button_used')}</div>;
        }
        return <></>;
    }, [receiveStatus, useStatus]);

    return (
        <div className={`${styled.Coupon}`}>
            <div className={`coupon-content ${receiveStatus === 2 && useStatus === 'USED' ? 'disabled' : ''}`}>
                <div className="name">{name}</div>
                {Button}
            </div>
        </div>
    );
};

export default Coupon;
